import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { fitCallUrl } from "@/lib/booking";
import { m, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PhotoBand } from "@/components/site/PhotoBand";
import { DiagramFrame, rise } from "@/components/diagrams/DiagramFrame";
import { services, type ServiceSlug } from "@/data/services";
import workshopImg from "@/assets/photos/workshop-floor.webp";
import leadershipTableImg from "@/assets/photos/leadership-table.webp";
import whiteboardPlanningImg from "@/assets/photos/whiteboard-planning.webp";
import scorecardImg from "@/assets/photos/operating-scorecard.webp";
import siteOfficePlansImg from "@/assets/photos/site-office-plans.webp";
import warehouseBayImg from "@/assets/photos/warehouse-bay.webp";
import staffBreakRoomImg from "@/assets/photos/staff-break-room.webp";
import { canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { NextStep } from "@/components/site/NextStep";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Core Services: Fractional COO Engagements | Ajility" },
      {
        name: "description",
        content:
          "Six service lines for founder-led Australian businesses: operating system design, people and accountability, strategy execution and systems improvement.",
      },
      { property: "og:title", content: "Core Services: Fractional COO Engagements | Ajility" },
      { property: "og:description", content: "Six service lines for founder-led Australian businesses: operating system design, people and accountability, strategy execution and systems improvement." },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Core Services: Fractional COO Engagements | Ajility" },
      { name: "twitter:description", content: "Six service lines for founder-led Australian businesses: operating system design, people and accountability, strategy execution and systems improvement." },
      ...ogImage("services", "Ajility core fractional COO services"),
      ogUrl("/services"),
    ],
    links: canonicalLinks("/services"),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Services", path: "/services" }])),
      jsonLd(webPageSchema({ image: "services",
        path: "/services",
        name: "Core Services: Fractional COO Engagements | Ajility",
        description: "Six service lines for founder-led Australian businesses: operating system design, people and accountability, strategy execution and systems improvement.",
        type: "CollectionPage",
      })),
    ],
  }),
  component: ServicesIndex,
});

/** One photograph per service line, described by what is actually in the frame. */
const servicePhotos: Record<ServiceSlug, { src: string; alt: string }> = {
  "partner-alignment": { src: leadershipTableImg, alt: "Business partners around a leadership table" },
  "structure-and-performance-reset": { src: whiteboardPlanningImg, alt: "Leaders mapping roles and responsibilities at a whiteboard" },
  "operating-system": {
    src: leadershipTableImg,
    alt: "Managers seated around a plain boardroom table with printed reports and laptops during a leadership meeting",
  },
  "team-structure": {
    src: staffBreakRoomImg,
    alt: "Four workshop staff in hi-vis shirts talking and laughing over mugs of tea at a staff kitchen table",
  },
  "strategy-execution": {
    src: whiteboardPlanningImg,
    alt: "Three people in a small meeting room working at a whiteboard filled with handwritten quarterly priorities and measures",
  },
  "performance-discipline": {
    src: scorecardImg,
    alt: "A printed operating scorecard on a desk beside a laptop showing weekly performance figures",
  },
  "project-leadership": {
    src: siteOfficePlansImg,
    alt: "Rolled and flattened project plans spread across a desk in a site office with hard hats nearby",
  },
  "systems-improvement": {
    src: warehouseBayImg,
    alt: "A warehouse dispatch bay with racked stock, labelled pallets and a forklift in the aisle",
  },
};

const NODES = services.filter(s => !["partner-alignment", "structure-and-performance-reset"].includes(s.slug)).map((s) => s.name);
const HUB_X = 700;
const HUB_Y = 320;
const NODE_W = 350;
const NODE_H = 62;

/** Six service lines feeding one central operating system. */
function ServiceMap() {
  const reduced = useReducedMotion() ?? false;

  return (
    <DiagramFrame
      captionClassName="italic"
      caption="Six service lines, one operating system. Buy one line or compose several."
    >
      <div className="space-y-3 sm:hidden">{NODES.map((name) => <div key={name} className="rounded-lg border border-border bg-card p-3 text-center text-sm font-semibold text-foreground">{name}</div>)}<p aria-hidden="true" className="text-center text-steel">↓</p><div className="rounded-lg bg-midnight p-4 text-center text-sm font-semibold text-frost">One installed operating system</div></div>
      <svg
        viewBox="0 0 1060 640"
        role="img"
        aria-labelledby="servicemap-title servicemap-desc"
        className="hidden h-auto w-full max-w-full sm:block"
      >
        <title id="servicemap-title">The six service lines feeding one operating system</title>
        <desc id="servicemap-desc">
          Six labelled service lines, {NODES.join("; ")}, each connecting into one central
          block labelled One installed operating system.
        </desc>

        {NODES.map((name, i) => {
          const y = 40 + i * 82;
          const cy = y + NODE_H / 2;
          return (
            <m.g key={name} {...rise(reduced, i * 0.05)}>
              <path
                d={`M ${40 + NODE_W} ${cy} C ${470} ${cy}, ${540} ${HUB_Y}, ${HUB_X - 20} ${HUB_Y}`}
                className="stroke-border"
                strokeWidth="2"
                fill="none"
              />
              <rect
                x="40"
                y={y}
                width={NODE_W}
                height={NODE_H}
                rx="14"
                className="fill-card stroke-border"
                strokeWidth="2"
              />
              <text
                x="62"
                y={cy + 5}
                fontSize="14"
                fontWeight="600"
                className="fill-foreground"
              >
                {name}
              </text>
            </m.g>
          );
        })}

        <m.g {...rise(reduced, 0.35)}>
          <rect
            x={HUB_X - 20}
            y={HUB_Y - 90}
            width="320"
            height="180"
            rx="24"
            className="fill-midnight"
          />
          <text
            x={HUB_X + 140}
            y={HUB_Y - 10}
            textAnchor="middle"
            fontSize="20"
            fontWeight="600"
            className="fill-frost"
          >
            One installed
          </text>
          <text
            x={HUB_X + 140}
            y={HUB_Y + 18}
            textAnchor="middle"
            fontSize="20"
            fontWeight="600"
            className="fill-frost"
          >
            operating system
          </text>
        </m.g>
      </svg>
    </DiagramFrame>
  );
}

function ServicesIndex() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <m.div
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-5xl px-5 sm:px-8"
        >
          <span className="eyebrow on-dark">What we do</span>
          <h1 className="mt-6 font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
            Fractional COO service lines and reviews for stronger operations.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-frost/75">
            Choose the part of the business that keeps getting stuck. Ajility installs clear ownership, a working rhythm and practical follow-through.
          </p>
          <div className="mt-10">
            <BookFitCall
              placement="services_index_hero"
              buttonClassName="inline-flex rounded-full bg-frost px-6 py-3 text-sm font-semibold text-midnight-deep hover:bg-white"
              noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
            />
          </div>
        </m.div>
      </section>
      <PhotoBand
        src={workshopImg}
        alt="Staff working at benches on a light-industrial workshop floor"
        priority
      />

      <section className="bg-background py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <ServiceMap />
        </div>
      </section>

      <section id="services" className="bg-mist py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <span className="eyebrow">Core services</span>
            <h2 className="mt-5 font-serif text-4xl leading-tight text-foreground sm:text-5xl text-balance">
              Fractional COO leadership across six operating functions.
            </h2>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => {
              const photo = servicePhotos[s.slug];
              return (
                <m.div
                  key={s.name}
                  initial={{ y: 20 }}
                  whileInView={{ y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                >
                  <Link to={s.to} className="group flex h-full flex-col overflow-hidden card-panel card-interactive">
                    <div className="-mx-7 -mt-7 mb-6 border-b border-border bg-midnight-deep sm:-mx-8 sm:-mt-8">
                      <img
                        src={photo.src}
                        alt={photo.alt}
                        width={1536}
                        height={864}
                        loading="lazy"
                        decoding="async"
                        className="h-44 w-full object-cover"
                      />
                    </div>
                    <div className="flex items-start justify-between">
                      <div className="grid h-12 w-12 place-items-center rounded-xl bg-midnight text-frost transition group-hover:bg-steel">
                        <s.icon className="h-5 w-5" />
                      </div>
                      <ArrowUpRight className="h-5 w-5 text-steel/60 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-steel" />
                    </div>
                    <h3 className="mt-8 font-serif text-2xl text-foreground">{s.name}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-charcoal">{s.details}</p>
                    <div className="mt-8 border-t border-border pt-5">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-steel">
                        Outcome
                      </span>
                      <p className="mt-1 font-serif text-lg text-foreground">{s.outcome}</p>
                    </div>
                  </Link>
                </m.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-mist py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <div className="rounded-3xl border border-border bg-card p-10 md:p-14">
            <span className="eyebrow">Process</span>
            <h2 className="mt-5 font-serif text-3xl text-foreground sm:text-4xl">
              One operating rhythm for every engagement
            </h2>
            <p className="mt-5 max-w-2xl text-charcoal">
               You know what Ajility will do, when it starts, how progress is checked and what your team owns at handover.
            </p>
            <div className="mt-8">
              <Link
                to="/how-we-work"
                className="inline-flex rounded-full bg-midnight px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-midnight-deep"
              >
                See our engagement process
              </Link>
            </div>
          </div>
        </div>
      </section>

      <NextStep
        heading="Not sure which service line to start with?"
        body="21 plain statements about how the business actually runs, answered from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="Book a fit call"
        secondaryTo={fitCallUrl("services_index_footer")}
        secondaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
      />
    </>
  );
}
